// src/hooks/useServerPagination.js
import { useState, useEffect, useCallback, useRef } from 'react';

export const useServerPagination = (
  fetchFunction, 
  initialParams = {}, 
  pageSize = 10,
  dataKey = null
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false); // 建议初始为 false，避免首次闪烁，或根据需求设为 true
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // 状态：筛选条件
  const [filters, setFilters] = useState(initialParams);
  // 状态：强制刷新触发器
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // 使用 Ref 来追踪当前的筛选条件，确保 fetchData 读到的是最新的
  // 这里在 render 期间赋值是可行的，确保 useEffect 执行时 ref 也是新的
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // 组装参数：页码 + 分页大小 + 当前的所有筛选条件
      const params = {
        page: currentPage,
        limit: pageSize,
        ...filtersRef.current 
      };

      console.log('📡 [Hook] Fetching:', params); // 调试日志，确认请求发出

      const response = await fetchFunction(params);
      
      // 数据提取逻辑
      let list = [];
      if (dataKey && Array.isArray(response[dataKey])) {
        list = response[dataKey];
      } else if (response.vocabulary) {
        // 针对特定的业务逻辑做一些特定兼容
        list = response.vocabulary;
      } else if (response.users) {
        list = response.users;
      } else {
        // 自动查找数组字段 (保底策略)
        const foundKey = Object.keys(response).find(key => Array.isArray(response[key]));
        list = foundKey ? response[foundKey] : [];
      }
      
      setData(list);
      setTotalItems(response.total || response.totalItems || 0);
      setTotalPages(response.totalPages || 0);
    } catch (error) {
      console.error('Pagination Fetch Error:', error);
      // 发生错误时清空数据，避免显示旧数据误导用户
      setData([]); 
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, currentPage, pageSize, dataKey, refreshTrigger]); // 🟢 依赖 refreshTrigger

  // 监听变化自动拉取
  // 当 currentPage 变了，或者 refreshTrigger 变了，都会触发
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==========================================
  // 🟢 核心修复：updateFilters
  // ==========================================
  const updateFilters = (newFilters) => {
    // 1. 更新筛选状态
    setFilters(prev => ({ ...prev, ...newFilters }));
    
    // 2. 重置页码逻辑 (修复 "在第1页搜索无效" 的 Bug)
    if (currentPage === 1) {
      // 如果本来就在第一页，改变 filters 不会触发 currentPage 变化
      // 所以必须手动拨动 refreshTrigger
      setRefreshTrigger(prev => prev + 1);
    } else {
      // 如果不在第一页，跳回第一页会自动触发 useEffect
      setCurrentPage(1);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  // 手动刷新 (比如删除某行数据后调用)
  const refresh = () => setRefreshTrigger(prev => prev + 1);

  return {
    data,
    loading,
    currentPage,
    totalPages,
    totalItems,
    goToPage,
    refresh,
    updateFilters
  };
};